import { createContext, useCallback, useContext } from "react";
import { type FieldErrors, get, useFormContext, useWatch } from "react-hook-form";
import type { OnboardingDraft } from "@/plan-logic/questionnaire/draft";
import { errorNames } from "./schema";

/** Server-side (engine) validation errors, by our form path, shown beside the field. */
export const ServerErrorsContext = createContext<Record<string, string>>({});

/**
 * The onboarding form (react-hook-form over the whole OnboardingDraft). Screens read the draft and
 * patch top-level keys immutably; errors come from the zod resolver (instant) or the engine.
 */
export function useDraft() {
  const form = useFormContext<OnboardingDraft>();
  const draft = useWatch({ control: form.control }) as OnboardingDraft;
  const server = useContext(ServerErrorsContext);
  const errors = form.formState.errors as FieldErrors<OnboardingDraft>;

  const patch = useCallback(
    <K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]) => {
      form.setValue(key as never, value as never, { shouldDirty: true });
      // Once a field has shown an error, re-check it as the user fixes it.
      const error = get(form.formState.errors, key as string);
      if (error) void form.trigger(errorNames(key as string, error) as never);
    },
    [form],
  );

  const err = useCallback(
    (path: string): string | undefined =>
      (get(errors, path)?.message as string | undefined) ?? server[path],
    [errors, server],
  );

  return { draft, patch, err, form };
}

/** Replace item `i` of a list with `fn(item)`. */
export function updateAt<T>(list: T[], i: number, fn: (item: T) => T): T[] {
  return list.map((x, k) => (k === i ? fn(x) : x));
}

export function removeAt<T>(list: T[], i: number): T[] {
  return list.filter((_, k) => k !== i);
}
